// src/components/ChangelogComponent.js
import React, { useState, useEffect } from 'react';
import { changelogService } from '../services/changelogService';

const ChangelogComponent = () => {
  const [isChangelogExpanded, setIsChangelogExpanded] = useState(false);
  const [changelogEntries, setChangelogEntries] = useState([]);
  const [isLoadingChangelog, setIsLoadingChangelog] = useState(true);
  const [changelogError, setChangelogError] = useState(null);

  useEffect(() => {
    loadChangelogEntries();
  }, []);

  const loadChangelogEntries = async () => {
    try {
      setIsLoadingChangelog(true);
      setChangelogError(null);
      const entries = await changelogService.getLatestChangelogEntries(5);
      setChangelogEntries(entries);
    } catch (error) {
      console.error('Error loading changelog:', error);
      setChangelogError('Failed to load changelog entries');
    } finally {
      setIsLoadingChangelog(false);
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'feature':
        return '✨';
      case 'improvement':
        return '🚀';
      case 'fix':
        return '🐛';
      default:
        return '📝';
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'feature':
        return 'text-green-600 bg-green-100';
      case 'improvement':
        return 'text-blue-600 bg-blue-100';
      case 'fix':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString) => {
    // Handle both string dates and Firestore timestamps
    let date;
    if (dateString && dateString.toDate) {
      // Firestore timestamp
      date = dateString.toDate();
    } else {
      // String date
      date = new Date(dateString);
    }
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Show loading state
  if (isLoadingChangelog) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4">
          <div className="flex items-center space-x-3">
            <span className="text-lg">🔔</span>
            <h3 className="text-lg font-semibold text-gray-800">What's New</h3>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (changelogError) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4">
          <div className="flex items-center space-x-3">
            <span className="text-lg">⚠️</span>
            <h3 className="text-lg font-semibold text-gray-800">What's New</h3>
            <span className="text-sm text-red-600">{changelogError}</span>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if no entries
  if (!changelogEntries || changelogEntries.length === 0) {
    return null;
  }

  const latestEntry = changelogEntries[0];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      {/* Collapsed Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
        onClick={() => setIsChangelogExpanded(!isChangelogExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <span className="text-lg">🔔</span>
              <h3 className="text-lg font-semibold text-gray-800">
                What's New
              </h3>
            </div>
            {!isChangelogExpanded && latestEntry && (
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(latestEntry.type)}`}>
                  {getTypeIcon(latestEntry.type)} {latestEntry.type}
                </span>
                <span className="text-sm text-gray-500">
                  v{latestEntry.version}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {!isChangelogExpanded && latestEntry && (
              <span className="text-sm text-gray-500">
                {latestEntry.title}
              </span>
            )}
            <svg 
              className={`w-5 h-5 text-gray-400 transform transition-transform duration-200 ${
                isChangelogExpanded ? 'rotate-180' : ''
              }`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isChangelogExpanded && (
        <div className="border-t border-gray-200">
          <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
            {changelogEntries.map((entry, index) => (
              <div key={entry.id || index} className="flex space-x-4">
                <div className="flex-shrink-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${getTypeColor(entry.type)}`}>
                    {getTypeIcon(entry.type)}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="text-sm font-semibold text-gray-900">
                      {entry.title}
                    </h4>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      v{entry.version}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    {entry.description}
                  </p>
                  <time className="text-xs text-gray-400">
                    {formatDate(entry.date)}
                  </time>
                </div>
              </div>
            ))}
          </div>
          

        </div>
      )}
    </div>
  );
};

export default ChangelogComponent;